import {
  Content,
  ContentHeader,
  Header,
  InfoCard,
  Page,
  Progress,
} from '@backstage/core-components';
import {
  configApiRef,
  type IdentityApi,
  useApi,
} from '@backstage/frontend-plugin-api';
import type { SignInPageProps } from '@backstage/plugin-app-react';
import { Box, Button, Chip, Grid, Typography } from '@material-ui/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ACTIVE_AUTH_PROVIDER_KEY,
  clearGuestSessionHint,
  clearLegacyGuestFallbackState,
  clearStoredGuestSelection,
  hasGuestProvider,
  hasGuestSessionHint,
  hasKeycloakProvider,
  isBrowser,
  markGuestSessionActive,
  SIGN_IN_PROVIDER_KEY,
} from './guestAuth';
import { keycloakAuthApiRef } from './keycloakAuthApi';

const KEYCLOAK_PROVIDER_ID = 'oidc';

type GuestSessionPayload = {
  profile?: {
    displayName?: string;
    email?: string;
    picture?: string;
  };
  backstageIdentity: {
    token: string;
    identity: {
      type: 'user';
      userEntityRef: string;
      ownershipEntityRefs: string[];
    };
  };
};

async function fetchGuestSession(
  backendBaseUrl: string,
): Promise<GuestSessionPayload> {
  const response = await window.fetch(
    `${backendBaseUrl}/api/auth/guest/refresh`,
    {
      credentials: 'include',
    },
  );
  if (!response.ok) {
    throw new Error(`Guest sign-in failed with HTTP ${response.status}`);
  }

  const payload = await response.json();
  if (!payload?.backstageIdentity?.token) {
    throw new Error('Guest sign-in did not return a Backstage identity.');
  }

  return payload;
}

function createGuestIdentityApi({
  backendBaseUrl,
  initialPayload,
}: {
  backendBaseUrl: string;
  initialPayload: GuestSessionPayload;
}): IdentityApi {
  let currentPayload = initialPayload;

  const refresh = async () => {
    currentPayload = await fetchGuestSession(backendBaseUrl);
    return currentPayload;
  };

  return {
    async getProfileInfo() {
      const profile = currentPayload.profile ?? {};
      return {
        displayName: profile.displayName ?? 'Guest',
        email: profile.email,
        picture: profile.picture,
      };
    },
    async getBackstageIdentity() {
      return currentPayload.backstageIdentity.identity;
    },
    async getCredentials() {
      const payload = await refresh();
      return { token: payload.backstageIdentity.token };
    },
    async signOut() {
      clearStoredGuestSelection();
    },
  };
}

function createOAuthIdentityApi({
  authApi,
  providerId,
}: {
  authApi: {
    getBackstageIdentity(options?: {
      instantPopup?: boolean;
      optional?: boolean;
    }): Promise<
      | {
          token: string;
          identity: Awaited<ReturnType<IdentityApi['getBackstageIdentity']>>;
        }
      | undefined
    >;
    getProfile(): Promise<
      Awaited<ReturnType<IdentityApi['getProfileInfo']>> | undefined
    >;
    signOut?(): Promise<void>;
  };
  providerId: string;
}): IdentityApi {
  return {
    async getProfileInfo() {
      return (
        (await authApi.getProfile()) ?? {
          displayName: 'C4CI user',
        }
      );
    },
    async getBackstageIdentity() {
      const identityResponse = await authApi.getBackstageIdentity();
      if (!identityResponse) {
        throw new Error('C4CI session is no longer available.');
      }
      return identityResponse.identity;
    },
    async getCredentials() {
      const identityResponse = await authApi.getBackstageIdentity();
      return { token: identityResponse?.token };
    },
    async signOut() {
      if (isBrowser()) {
        window.localStorage.removeItem(SIGN_IN_PROVIDER_KEY);
        if (
          window.localStorage.getItem(ACTIVE_AUTH_PROVIDER_KEY) === providerId
        ) {
          window.localStorage.removeItem(ACTIVE_AUTH_PROVIDER_KEY);
        }
      }
      await authApi.signOut?.();
    },
  };
}

export const FactoryPortalSignInPage = ({
  onSignInSuccess,
}: SignInPageProps) => {
  const configApi = useApi(configApiRef);
  const keycloakAuthApi = useApi(keycloakAuthApiRef);
  const backendBaseUrl = configApi
    .getString('backend.baseUrl')
    .replace(/\/$/, '');
  const keycloakProviderEnabled = hasKeycloakProvider(configApi);
  const guestProviderEnabled = hasGuestProvider(configApi);
  const errorRef = useRef<HTMLElement | null>(null);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (error) {
      errorRef.current?.focus();
    }
  }, [error]);

  const signInWithGuest = useCallback(async () => {
    setIsSigningIn(true);
    setError(undefined);
    try {
      const payload = await fetchGuestSession(backendBaseUrl);
      if (isBrowser()) {
        window.localStorage.setItem(SIGN_IN_PROVIDER_KEY, 'guest');
      }
      markGuestSessionActive();
      onSignInSuccess(
        createGuestIdentityApi({
          backendBaseUrl,
          initialPayload: payload,
        }),
      );
    } catch (err) {
      clearStoredGuestSelection();
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsSigningIn(false);
    }
  }, [backendBaseUrl, onSignInSuccess]);

  const signInWithKeycloak = useCallback(
    async (options?: { restoreOnly?: boolean }) => {
      setIsSigningIn(true);
      setError(undefined);
      try {
        if (!options?.restoreOnly && isBrowser()) {
          window.localStorage.setItem(
            SIGN_IN_PROVIDER_KEY,
            KEYCLOAK_PROVIDER_ID,
          );
          window.localStorage.setItem(
            ACTIVE_AUTH_PROVIDER_KEY,
            KEYCLOAK_PROVIDER_ID,
          );
        }

        const identityResponse = await keycloakAuthApi.getBackstageIdentity({
          instantPopup: !options?.restoreOnly,
          optional: options?.restoreOnly,
        });
        if (!identityResponse) {
          if (!options?.restoreOnly) {
            throw new Error('C4CI login did not return a Backstage identity.');
          }
          return;
        }

        if (isBrowser()) {
          window.localStorage.setItem(
            SIGN_IN_PROVIDER_KEY,
            KEYCLOAK_PROVIDER_ID,
          );
          window.localStorage.setItem(
            ACTIVE_AUTH_PROVIDER_KEY,
            KEYCLOAK_PROVIDER_ID,
          );
        }
        clearGuestSessionHint();
        onSignInSuccess(
          createOAuthIdentityApi({
            authApi: keycloakAuthApi,
            providerId: KEYCLOAK_PROVIDER_ID,
          }),
        );
      } catch (err) {
        if (!options?.restoreOnly) {
          if (isBrowser()) {
            window.localStorage.removeItem(SIGN_IN_PROVIDER_KEY);
            if (
              window.localStorage.getItem(ACTIVE_AUTH_PROVIDER_KEY) ===
              KEYCLOAK_PROVIDER_ID
            ) {
              window.localStorage.removeItem(ACTIVE_AUTH_PROVIDER_KEY);
            }
          }
          setError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        setIsSigningIn(false);
      }
    },
    [keycloakAuthApi, onSignInSuccess],
  );

  useEffect(() => {
    clearLegacyGuestFallbackState();

    if (!isBrowser()) {
      return;
    }

    const selectedProvider = window.localStorage.getItem(SIGN_IN_PROVIDER_KEY);
    if (
      guestProviderEnabled &&
      (selectedProvider === 'guest' || hasGuestSessionHint())
    ) {
      void signInWithGuest();
      return;
    }

    if (keycloakProviderEnabled && selectedProvider === KEYCLOAK_PROVIDER_ID) {
      void signInWithKeycloak({ restoreOnly: true });
      return;
    }

    if (selectedProvider !== 'guest') {
      clearGuestSessionHint();
    }
  }, [
    keycloakProviderEnabled,
    guestProviderEnabled,
    signInWithKeycloak,
    signInWithGuest,
  ]);

  return (
    <Page themeId="home">
      <Header title={configApi.getString('app.title')} />
      <Content>
        <ContentHeader
          title="Sign in"
          textAlign="left"
          titleComponent={
            <Box maxWidth={540}>
              <Box display="flex" flexWrap="wrap" gridGap={8} mb={2}>
                <Chip color="primary" label="C4CI Factory Portal" />
                <Chip label="C4CI Account" variant="outlined" />
                <Chip label="Repo-owned write path" variant="outlined" />
                <Chip label="Governed internal runtime" variant="outlined" />
              </Box>
              <Typography variant="h3" component="h1" gutterBottom>
                Operate the product factory through real services and governed
                workflows.
              </Typography>
              <Typography variant="body1" color="textSecondary">
                Use the same C4CI account login as Admin Hub. Guest access
                remains a local-development escape hatch only, so we can keep
                local setup fast without weakening the production path.
              </Typography>
            </Box>
          }
        />
        {error ? (
          <Box mb={2}>
            <Typography color="error" role="alert" tabIndex={-1} ref={errorRef}>
              {error}
            </Typography>
          </Box>
        ) : null}
        <Grid container spacing={2} component="ul">
          {keycloakProviderEnabled ? (
            <Grid item xs={12} md={4} component="li">
              <InfoCard
                variant="fullHeight"
                title="C4CI Account"
                actions={
                  <Button
                    color="primary"
                    variant="outlined"
                    onClick={() => {
                      void signInWithKeycloak();
                    }}
                    disabled={isSigningIn}
                  >
                    Sign in
                  </Button>
                }
              >
                <Typography variant="body1">
                  Same shared Keycloak login used by Admin Hub and the C4CI
                  product surfaces.
                </Typography>
              </InfoCard>
            </Grid>
          ) : null}
          {guestProviderEnabled ? (
            <Grid item xs={12} md={4} component="li">
              <InfoCard
                variant="fullHeight"
                title="Guest"
                actions={
                  <Button
                    color="primary"
                    variant="outlined"
                    onClick={() => {
                      void signInWithGuest();
                    }}
                    disabled={isSigningIn}
                  >
                    Enter
                  </Button>
                }
              >
                <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
                  Enter as a Guest User.{'\n'}You will not have a verified
                  identity, meaning some features might be unavailable.
                </Typography>
              </InfoCard>
            </Grid>
          ) : null}
        </Grid>
        {isSigningIn ? <Progress /> : null}
      </Content>
    </Page>
  );
};
